/**
 * Adapters — turn the server-authoritative `toc` / `section` Inertia props
 * (see ./types.ts) into the `ReaderRouteSection[]` / `ReaderRouteParent[]` /
 * `LoadedSection` shapes that `@arianna/reader-ui`'s `TocSidebar` /
 * `ReaderChrome` / `ReaderDocument` expect.
 *
 * WHY this indirection exists (read before touching): those reader-ui
 * components were built against the FAT client, which held a full
 * `BundleManuale` in memory and derived everything (including per-section
 * `completed`/`review` flags) by walking real `subParagraphs`. The thin
 * client (TASK-009 Phase 5) never has the full manuale — the server sends
 * only a TOC *skeleton* (id/slug/title/kind + already-computed
 * completed/review flags per section) and, separately, the CURRENT section's
 * real content. Reusing the components without forking them (mandatory per
 * TASK-009 — mobile/desktop share reader-ui/reader-core) means synthesizing
 * minimal-but-type-correct stand-ins:
 *
 *   - Each TOC section becomes a `ReaderRouteSection` whose `node` carries a
 *     SINGLE synthetic sub-paragraph/keyPoints entry with `id` set to the
 *     section's own id. `completedIds`/`reviewIds` (the sets TocSidebar
 *     recomputes `isSectionCompleted`/`isSectionForReview` from) contain the
 *     section's id iff the server already flagged it completed/review. This
 *     reproduces the server's per-section boolean EXACTLY (see the
 *     isSectionCompleted/isSectionForReview definitions in
 *     packages/reader-core/src/reader/sections.ts) without inventing any new
 *     business logic — it's a pure representational trick, safe because
 *     TocSidebar only ever READS these sets for display; it never reports
 *     subParaIds back for a mutation (`onSelect` returns a flat index only).
 *   - The CURRENTLY LOADED section instead gets its REAL node, built from
 *     `section.content.parts` (real subParaIds/titles/blocks) — needed both
 *     for correct rendering (ReaderDocument) and because the topbar/footer
 *     "completed"/"review" toggle buttons must sync progress using the
 *     section's REAL subParaIds, not the synthetic ones (see Reader.tsx).
 */

import type {
  BundleManuale,
  BundleManualeChapter,
  BundleManualeCompendio,
  BundleManualeKeyPoints,
  BundleManualeSubTheme,
} from '@arianna/shared'
import type { ReaderRouteParent, ReaderRouteSection, LoadedSection } from '@arianna/reader-core'
import type { ReaderSectionProp, ReaderTocProp, TocChapterProp } from './types.js'

// ---------------------------------------------------------------------------
// EMPTY_MANUALE — stand-in BundleManuale for ReaderDocument's unused `manuale`
// prop (see packages/reader-ui/src/ReaderDocument.tsx: `manuale: _manuale` is
// never read in the component body — only kept for interface parity with the
// fat-client callers). `.min(1)` constraints on `chapters` are Zod RUNTIME
// refinements, not part of the inferred TS type, so an empty array type-checks.
// ---------------------------------------------------------------------------
export const EMPTY_MANUALE: BundleManuale = {
  schemaVersion: 3,
  bundleSlug: 'web-thin-client',
  bundleVersion: '0',
  title: '',
  subtitle: null,
  author: '',
  createdAt: new Date(0).toISOString(),
  compendio: null,
  chapters: [],
}

// ---------------------------------------------------------------------------
// Flattened TOC section — same order/index alignment as the ReaderRouteSection[]
// this module builds, but keeps the fields (slug, completed, review) that
// ReaderRouteSection has no room for.
// ---------------------------------------------------------------------------

export interface FlatTocSection {
  id: string
  slug: string
  title: string
  kind: 'subTheme' | 'keyPoints'
  completed: boolean
  review: boolean
  parentId: string
  parentTitle: string
}

export interface TocAdapterResult {
  routeSections: ReaderRouteSection[]
  routeParents: ReaderRouteParent[]
  flatSections: FlatTocSection[]
  completedIds: Set<string>
  reviewIds: Set<string>
}

function isCompendioChapter(chapter: TocChapterProp): boolean {
  return chapter.id === 'compendio'
}

function syntheticNode(
  kind: 'subTheme' | 'keyPoints',
  id: string,
  title: string,
): BundleManualeSubTheme | BundleManualeKeyPoints {
  if (kind === 'keyPoints') {
    return { kind: 'keyPoints', id, title, blocks: [] }
  }
  return {
    kind: 'subTheme',
    id,
    title,
    subParagraphs: [{ kind: 'subParagraph', id, title, blocks: [] }],
  }
}

/**
 * Builds the full TOC adapter (sections, parents, completed/review sets) from
 * the `toc` Inertia prop alone — no manuale, no per-section content beyond
 * what the TOC skeleton already carries (id/slug/title/kind/completed/review).
 */
export function buildTocAdapter(toc: ReaderTocProp): TocAdapterResult {
  const routeSections: ReaderRouteSection[] = []
  const routeParents: ReaderRouteParent[] = []
  const flatSections: FlatTocSection[] = []
  const completedIds = new Set<string>()
  const reviewIds = new Set<string>()

  for (const chapter of toc.chapters) {
    const isCompendio = isCompendioChapter(chapter)
    routeParents.push(
      isCompendio
        ? {
            kind: 'compendio',
            node: {
              kind: 'compendio',
              id: 'compendio',
              title: chapter.title,
              subThemes: [],
            } satisfies BundleManualeCompendio,
          }
        : {
            kind: 'chapter',
            node: {
              kind: 'chapter',
              id: chapter.id,
              title: chapter.title,
              subThemes: [],
              keyPoints: null,
            } satisfies BundleManualeChapter,
          },
    )

    for (const section of chapter.sections) {
      if (section.completed) completedIds.add(section.id)
      if (section.review) reviewIds.add(section.id)

      flatSections.push({
        id: section.id,
        slug: section.slug,
        title: section.title,
        kind: section.kind,
        completed: section.completed,
        review: section.review,
        parentId: chapter.id,
        parentTitle: chapter.title,
      })

      routeSections.push({
        id: section.id,
        kind: section.kind,
        node: syntheticNode(section.kind, section.id, section.title),
        parentKind: isCompendio ? 'compendio' : 'chapter',
        parentId: chapter.id,
        parentTitle: chapter.title,
        parentNumericLabel: isCompendio ? null : chapter.id,
        flatIndex: routeSections.length,
      })
    }
  }

  return { routeSections, routeParents, flatSections, completedIds, reviewIds }
}

// ---------------------------------------------------------------------------
// Current section adapter — builds the REAL ReaderRouteSection + LoadedSection
// for the section the server just sent (content.parts has real subParaIds).
// ---------------------------------------------------------------------------

export interface CurrentSectionAdapterResult {
  routeSection: ReaderRouteSection
  loadedSection: LoadedSection
  /** Real subParaIds of the current section (content.parts[].id) — the ONLY
   *  ids safe to sync progress/review against (see module doc). */
  subParaIds: string[]
}

export function buildCurrentSectionAdapter(
  toc: TocAdapterResult,
  section: ReaderSectionProp,
): CurrentSectionAdapterResult {
  const flat = toc.flatSections.find((s) => s.slug === section.slug)
  const kind: 'subTheme' | 'keyPoints' = flat?.kind ?? 'subTheme'
  const parentId = flat?.parentId ?? 'compendio'
  const parentTitle = flat?.parentTitle ?? ''
  const isCompendio = parentId === 'compendio'

  const subParaIds = section.content.parts.map((p) => p.id)

  const node: BundleManualeSubTheme | BundleManualeKeyPoints =
    kind === 'keyPoints'
      ? {
          kind: 'keyPoints',
          id: section.section_id,
          title: section.title,
          blocks: section.content.parts[0]?.blocks ?? [],
        }
      : {
          kind: 'subTheme',
          id: section.section_id,
          title: section.title,
          subParagraphs: section.content.parts.map((p) => ({
            kind: 'subParagraph' as const,
            id: p.id,
            title: p.title,
            blocks: p.blocks,
          })),
        }

  const routeSection: ReaderRouteSection = {
    id: section.section_id,
    kind,
    node,
    parentKind: isCompendio ? 'compendio' : 'chapter',
    parentId,
    parentTitle,
    parentNumericLabel: isCompendio ? null : parentId,
    flatIndex: toc.flatSections.findIndex((s) => s.slug === section.slug),
  }

  const parts =
    kind === 'keyPoints'
      ? [
          {
            kind: 'keyPoints' as const,
            id: section.content.parts[0]?.id ?? section.section_id,
            title: section.content.parts[0]?.title ?? section.title,
            blocks: section.content.parts[0]?.blocks ?? [],
          },
        ]
      : section.content.parts.map((p) => ({
          kind: 'subParagraph' as const,
          id: p.id,
          title: p.title,
          blocks: p.blocks,
        }))

  return {
    routeSection,
    loadedSection: { section: routeSection, parts },
    subParaIds,
  }
}

// ---------------------------------------------------------------------------
// findRouteSectionBySlug — used to build prev/next ReaderRouteSection for
// ReaderChrome (topbar/footer) from `section.prev_slug` / `section.next_slug`.
// ---------------------------------------------------------------------------

export function findRouteSectionBySlug(
  toc: TocAdapterResult,
  slug: string | null,
): ReaderRouteSection | null {
  if (slug == null) return null
  const idx = toc.flatSections.findIndex((s) => s.slug === slug)
  return idx >= 0 ? (toc.routeSections[idx] ?? null) : null
}

// ---------------------------------------------------------------------------
// findSectionForSubParaId — resolves a fine-grained subParaId (e.g. from a
// MasteryReport's `reviewSubParaId`, which may point at a sub-paragraph
// WITHIN a section, not the section's own id) to the TOC section that
// contains it.
//
// The thin client has no full manuale to walk (that's what the fat client
// did — findSectionContainingSubParaId in reader-core). Instead this relies
// on the DOCUMENTED hierarchical id scheme (TASK-009: section ids and their
// sub-paragraph ids share a dotted prefix, e.g. section '1.1' contains
// sub-paragraphs '1.1.1', '1.1.2', ...): exact id match first, then longest
// dotted-prefix match. Purely client-side, no backend change needed.
// ---------------------------------------------------------------------------

export function findSectionForSubParaId(
  toc: TocAdapterResult,
  subParaId: string,
): FlatTocSection | null {
  const exact = toc.flatSections.find((s) => s.id === subParaId)
  if (exact != null) return exact

  let best: FlatTocSection | null = null
  for (const section of toc.flatSections) {
    if (!subParaId.startsWith(`${section.id}.`)) continue
    if (best == null || section.id.length > best.id.length) best = section
  }
  return best
}
